import { yupResolver } from "@hookform/resolvers/yup";
import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";
import * as yup from "yup";
import AppInputNew from "../../components/Core/AppInput/AppInputNew";
import { AppSelectController } from "../../components/Core/AppSelect/AppSelect";
import ButtonNew from "../../components/Core/Button/ButtonNew";
import CheckboxNew from "../../components/Core/Checkbox/CheckboxNew";
import { FormCard } from "../../components/Core/FormCard";
import LoadingIndicator from "../../components/Shared/LoadingIndicator/LoadingIndicator";
import { FEEDBACK_MESSAGE_TYPE } from "../../configs/feedbackMessages";
import { enable2FARequest, UpdateUserRequest } from "../../models/InternalUser";
import {
	useCreateInternalUser,
	useDisable2FA,
	useEnable2FA,
	useInternalUser,
	useUpdateInternalUser,
} from "../../networking/internal-user.hooks";
import { routes } from "../../configs/constants/routes";
import { toast } from "../../utils/toast";
import { useOrganisation } from "../../networking/organisation.hooks";

interface UserParamProps {
	userId: string;
}

interface UserFormPropTypes {
	isEdit?: boolean;
	nextStep: () => void;
	setOtpEnabled: (value: boolean) => void;
	setVerificationId: (value: string) => void;
	setPhoneNumber: (value: string) => void;
	otpEnabled: boolean;
	setTimer: (value: number) => void;
	setUserId?: (value: string) => void;
	refreshPage?: () => void;
	otpStatus?: boolean;
}

const COUNTRY_CODE = "+46";

const userFormUValidationSchema = yup.object().shape({
	firstName: yup.string().required("FIELD_IS_REQUIRED"),
	lastName: yup.string().required("FIELD_IS_REQUIRED"),
	email: yup.string().required("FIELD_IS_REQUIRED").email(),
	roles: yup.mixed().required("FIELD_IS_REQUIRED"),
});

const UserForm: React.FC<UserFormPropTypes> = ({
	isEdit = false,
	nextStep,
	setOtpEnabled,
	otpEnabled,
	setVerificationId,
	setPhoneNumber,
	setTimer,
	setUserId,
	refreshPage,
	otpStatus,
}) => {
	const { userId } = useParams<keyof UserParamProps>() as UserParamProps;

	const { user, isInitialLoading: isLoadingUser } = useInternalUser(isEdit ? userId : undefined);
	const { mutateAsync: updateInternalUser, isLoading: isUpdating } = useUpdateInternalUser();
	const { mutateAsync: createInternalUser, isLoading: isCreating } = useCreateInternalUser();
	const { mutateAsync: enable2FA, isLoading: isEnabling2FA } = useEnable2FA();
	const { mutateAsync: disable2FA } = useDisable2FA();

	const { t } = useTranslation();
	const navigate = useNavigate();
	const { data: organisation } = useOrganisation();

	const ROLES = [
		{ key: "organisation_admin", value: t("ORGANISATION_ADMIN") },
		{ key: "organisation_consultant_manager", value: t("ORGANISATION_CONSULTANT_MANAGER") },
	];

	const { register, handleSubmit, reset, control, formState, trigger } = useForm<UpdateUserRequest>(
		{
			defaultValues: isEdit ? user : { roles: [ROLES[0].key] },
			mode: "onBlur",
			resolver: yupResolver(userFormUValidationSchema),
		}
	);

	useEffect(() => {
		if (isEdit && user) {
			reset(user);
		}
	}, [reset, user, isEdit]);

	// AppInputNew has no onBlur prop, so wire up the form's onBlur validation here.
	const textField = (name: "firstName" | "lastName" | "email" | "phone") => ({
		...register(name),
		onBlurText: () => void trigger(name),
	});

	const onSubmit = handleSubmit(async (e) => {
		const otpData = {
			status: otpStatus || false,
			enabled: otpEnabled,
		};
		const newUpdatedUser: UpdateUserRequest = {
			firstName: e.firstName,
			lastName: e.lastName,
			email: e.email,
			roles: e.roles,
		};

		let result!: any;

		if (isEdit) {
			// Turn off SMS 2FA when the checkbox was unchecked (this decision used
			// to live inside UsersStore.updateInternalUser).
			if (user?.twoFactor === "sms" && !otpData.enabled) {
				await disable2FA({ user: { id: userId } }).catch(() => undefined);
			}

			// Preserve the old store contract: true on success, the error otherwise.
			result = await updateInternalUser({ userId, update: newUpdatedUser })
				.then(() => true)
				.catch((e) => e);
		} else {
			newUpdatedUser.active = true;
			// Preserve the old store contract: { data } on success, the error otherwise.
			result = await createInternalUser(newUpdatedUser)
				.then((created) => ({ data: created }))
				.catch((e) => e);
		}
		if (result === true || result?.data?.id) {
			if (
				otpData.enabled &&
				e.phone &&
				detectOtpChange(otpData.status, user?.phone || "", e.phone)
			) {
				const phone = COUNTRY_CODE + parseInt(e.phone).toString();
				const enable2FARequest: enable2FARequest = {
					phone: phone || "",
					user: {
						id: userId || result?.data?.id,
					},
				};
				if (result?.data?.id && setUserId) {
					setUserId(result?.data?.id);
				}
				setPhoneNumber(phone || "");
				const otpResult: any = await enable2FA(enable2FARequest).catch((e) => e);
				if (otpResult.verificationId) {
					setTimer(otpResult?.ttl || 60);
					nextStep();
					setVerificationId(otpResult.verificationId);
				} else {
					toast.error(FEEDBACK_MESSAGE_TYPE.SOMETHING_WENT_WRONG);
				}
			} else {
				if (result && result?.data?.id) {
					toast.success(FEEDBACK_MESSAGE_TYPE.CHANGES_SAVE_SUCCESSFULLY);
					if (refreshPage) refreshPage();
					if (!isEdit) navigate(routes.INTERNAL_USERS);
				} else {
					if (result === true) {
						toast.success(FEEDBACK_MESSAGE_TYPE.CHANGES_SAVE_SUCCESSFULLY);
						if (refreshPage) refreshPage();
						if (!isEdit) navigate(routes.INTERNAL_USERS);
					} else if (result?.error?.code === "fruster-user-service.ID_NOT_UNIQUE") {
						toast.error(FEEDBACK_MESSAGE_TYPE.ANOTHER_ACCOUNT_FOUND_ERROR_MESSAGE);
					} else {
						toast.error(FEEDBACK_MESSAGE_TYPE.SOMETHING_WENT_WRONG);
					}
				}
			}
		} else {
			if (result?.error?.code === "fruster-user-service.ID_NOT_UNIQUE") {
				toast.error(FEEDBACK_MESSAGE_TYPE.ANOTHER_ACCOUNT_FOUND_ERROR_MESSAGE);
			} else {
				toast.error(FEEDBACK_MESSAGE_TYPE.SOMETHING_WENT_WRONG);
			}
		}
	});

	function detectOtpChange(status: boolean, userPhone: string, formPhone: string): boolean {
		if (status) {
			if (userPhone !== formPhone) {
				return true;
			} else {
				return false;
			}
		} else {
			if (formPhone) {
				return true;
			} else {
				return false;
			}
		}
	}

	return (
		<div>
			{isLoadingUser || isUpdating || isCreating || isEnabling2FA ? (
				<LoadingIndicator />
			) : (
				<form noValidate onSubmit={onSubmit} className="mx-auto mb-16 mt-8 w-full max-w-[720px]">
					<div className="flex flex-col gap-5">
						<FormCard title="BASIC_INFO">
							<div className="flex flex-col gap-4">
								<div className="grid grid-cols-1 gap-4 md:grid-cols-2">
									<AppInputNew
										{...textField("firstName")}
										label="FIRST_NAME"
										placeholder="FIRST_NAME"
										errors={formState.errors?.firstName?.message}
										mandatory
									/>
									<AppInputNew
										{...textField("lastName")}
										label="LAST_NAME"
										placeholder="LAST_NAME"
										errors={formState.errors?.lastName?.message}
										mandatory
									/>
								</div>
								<AppInputNew
									{...textField("email")}
									type="email"
									label="EMAIL"
									placeholder="EMAIL"
									errors={formState.errors?.email?.message}
									mandatory
								/>
								<AppSelectController<{ key: string; value: string }>
									control={control}
									label="ROLE"
									name="roles[0]"
									options={ROLES}
									getOptionLabel={(option) => option.value}
									getOptionValue={(option) => option.key}
									errors={formState.errors?.roles?.message}
									placeholder="SELECT_ROLE"
									margin="0"
									mandatory
								/>
							</div>
						</FormCard>

						<FormCard title="otp2FA.formSectionTitle">
							<div className="flex flex-col gap-4">
								<AppInputNew
									{...textField("phone")}
									type="tel"
									label="PHONE"
									placeholder="PHONE"
									prefix={COUNTRY_CODE}
									icon="swedenFlag"
									errors={formState.errors?.phone?.message}
								/>
								<CheckboxNew
									label="otp2FA.formEnableLabel"
									hint={
										organisation?.securitySettings?.requireTwoFactor
											? "otp2FA.formOrganisationRequiresHint"
											: undefined
									}
									hintClassName="max-w-prose"
									checked={otpEnabled}
									onCheckedChange={setOtpEnabled}
								/>
							</div>
						</FormCard>

						<div className="flex justify-end">
							<ButtonNew type="submit">{isEdit ? t("SAVE_CHANGES") : t("CREATE_USER")}</ButtonNew>
						</div>
					</div>
				</form>
			)}
		</div>
	);
};

export default UserForm;
